import { ItineraryFragmentFragment } from "../../types";
import { i18nLanguage } from "i18n";
import { DistanceUnit } from "shared/types";
import { formatDistance } from "shared/utils/formatDistance";
import { getLocalizedString } from "../../utils/getLocalizedString";
import { normalizeRichTextHtml } from "../../utils/normalizeRichTextHtml";
import { useAtlasMode } from "../../context/TripperWidgetContext";
import { getCoverMedia } from "../../utils/getCoverMedia";
import { MutedMuxPreview } from "shared/components/molecules/MutedMuxPreview";


const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v13M5 12l7 7 7-7M5 21h14" />
  </svg>
);

const SpinnerIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="tripper-download-spinner">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25" />
    <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

type TripperHeaderProps = {
  itinerary: ItineraryFragmentFragment;
  language: i18nLanguage;
  segmentCount: number;
  stopCount: number;
  isDayTrip?: boolean;
  unit?: DistanceUnit;
  onDownloadPdf?: () => void;
  isGeneratingPdf?: boolean;
};

export const TripperHeader = ({
  itinerary,
  language,
  segmentCount,
  stopCount,
  isDayTrip = false,
  unit = "metric",
  onDownloadPdf,
  isGeneratingPdf = false,
}: TripperHeaderProps) => {
  const atlasMode = useAtlasMode();
  const name = getLocalizedString(itinerary.name, language);
  const rawDescription = itinerary.description
    ? getLocalizedString(itinerary.description, language)
    : "";
  const description = normalizeRichTextHtml(rawDescription);
  const formattedDistance =
    itinerary.routeDistance ? formatDistance(itinerary.routeDistance, unit) : null;

  const coverMedia = getCoverMedia(itinerary.coverImage);
  const organizationName = itinerary.organization?.name
    ? getLocalizedString(itinerary.organization.name, language)
    : null;

  return (
    <div className="tripper-hero tripper-metadata">
      {!atlasMode && (
        coverMedia?.kind === "mux-video" ? (
          <div className="tripper-hero__image tripper-hero__image--video" aria-label={name}>
            <MutedMuxPreview playbackId={coverMedia.playbackId} active />
          </div>
        ) : coverMedia?.kind === "image" ? (
          <img src={coverMedia.url} alt={name} className="tripper-hero__image" />
        ) : (
          <div
            className="tripper-hero__image"
            style={{
              background: "var(--tripper-bg-secondary)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 250,
            }}
          />
        )
      )}

      <div className="tripper-hero__body">
        <div className="tripper-hero__meta">
          {organizationName && <span className="tripper-hero__subtitle">{organizationName}</span>}
          <h1 className="tripper-hero__title">{name}</h1>

          {!atlasMode && (
          <div className="tripper-hero__stats">
            {itinerary.travelType && (
              <span className="tripper-tag">{itinerary.travelType.charAt(0).toUpperCase() + itinerary.travelType.slice(1).toLowerCase()}</span>
            )}
            <div className="tripper-hero__stats-text">
              <span>
                {isDayTrip ? "Day trip" : `${segmentCount} ${segmentCount === 1 ? "day" : "days"}`}
              </span>
              {formattedDistance && (
                <>
                  <span className="tripper-hero__stats-divider">|</span>
                  <span>{formattedDistance}</span>
                </>
              )}
              <span className="tripper-hero__stats-divider">|</span>
              <span>
                {stopCount} {stopCount === 1 ? "stop" : "stops"}
              </span>
            </div>
          </div>
          )}
        </div>

        {description && (
          <div
            className="tripper-hero__description"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}

        {onDownloadPdf && (
          <button
            type="button"
            className="tripper-hero__download"
            onClick={onDownloadPdf}
            disabled={isGeneratingPdf}
          >
            {isGeneratingPdf ? <SpinnerIcon /> : <DownloadIcon />}
            {isGeneratingPdf ? "Downloading…" : "Download PDF"}
          </button>
        )}
      </div>
    </div>
  );
};
